import type { LocationMenu as ApiMenu } from 'api/locations'
import { buildTranslate } from 'locales'
import type { OrderingProvider } from 'models/Merchant'
import { getDateWithTimeAndZone } from 'utilities/date'
import type { LocationIssue, LocationMenu } from '../mockLocationDetails'
import locationErrorCopy from './locationErrorCopy'

const t = buildTranslate('locations.location_list.location_detail.issues')

// Operator-facing display name per integrated ordering provider. Keyed on
// OrderingProvider (not a loose string) so this stays exhaustive — adding a
// provider to the model type forces a display name here at compile time
// rather than silently falling through to the generic label.
const PROVIDER_DISPLAY_NAMES: Record<OrderingProvider, string> = {
  toast: 'Toast',
  olo: 'Olo',
  deliverect: 'Deliverect',
  square: 'Square',
  qu: 'Qu',
}

// Render in the location's IANA timezone if known, otherwise UTC, so two
// operators in different timezones see the same value on a "last synced"
// surface.
// Returns null (not an em dash) when the location has never synced, so
// the caller can render honest "Not yet synced" copy instead of the
// broken-reading "Last updated —."
export const formatLastUpdated = (
  iso: string | null,
  timezone: string | undefined
): string | null =>
  iso ? getDateWithTimeAndZone(iso, timezone || 'UTC') : null

// Treat the legacy flat config_errors as all-errors (no severity tier
// without DATA-5223's classifier). Once the operations-center-classified-errors
// flag flips on, swap to the classified errors/notices arrays from the
// response. Known validator strings get the Figma 5:1028 human title +
// guidance pair (DATA-5333); unknown strings keep the raw message.
export const toIssues = (
  raw: string[],
  orderingProvider?: OrderingProvider | null
): LocationIssue[] => {
  const providerName = orderingProvider
    ? PROVIDER_DISPLAY_NAMES[orderingProvider]
    : undefined
  const source = providerName
    ? (t('source_provider', { provider: providerName }) as string)
    : (t('source_generic') as string)

  return raw.map((message, idx) => {
    const copy = locationErrorCopy(message)
    return {
      id: `cfg-error-${idx}`,
      level: 'error',
      title: copy?.title ?? message,
      // Left undefined (not '') when the string has no guidance pair, so
      // IssuePanel omits the subtitle paragraph entirely.
      description: copy?.guidance,
      source,
    }
  })
}

export const toMenu = (m: ApiMenu): LocationMenu => ({
  id: String(m.id),
  name: m.name,
})
